import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import ModalHistorico from '@/modules/system/historico/ModalHistorico';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { CreateSetorDto, ReadPersonDto, ReadPredioDto, ReadSetorDto } from '@/api.types';
import { DeleteSetor, GetSetor, SetSetor } from '@/modules/structure/setor/setor.service';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { GetPredios } from '@/modules/structure/predio/predio.service';
import { useNavigate, useParams } from 'react-router-dom';
import { SelectGenerico } from '@/shared/components/forms/SelectGenerico';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Tabelas } from '@/shared/constants/tables';
import { readPerson } from '@/modules/people/person.service';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';

export default function SetorEditPage() {
  const { setActions } = useBreadcrumbActions();
  const navigate = useNavigate();
  const params = useParams();

  const [modalExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', href: Links.Setor_Criar, onClick: () => {} },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions]);

  const [loaded, setLoaded] = useState(false);
  const [predios, setPredios] = useState<ReadPredioDto[]>([]);
  const [pessoas, setPessoas] = useState<ReadPersonDto[]>([]);

  const { register, handleSubmit, reset, watch, setValue } = useForm<ReadSetorDto>({
    defaultValues: {
      sigla: '',
      nome: '',
      capacidade: 0,
      predio: undefined,
      responsavel: undefined,
    },
  });

  const responsavel = watch('responsavel');
  const editadoPor = watch('editadoPor');
  const editadoEm = watch('editadoEm');

  useEffect(() => {
    Promise.all([
      GetPredios(0, 1000),
      readPerson({ skip: 0, take: 1000 }),
      id > 0
        ? GetSetor(id)
          .then((result) => {
            if (result) {
              reset(result);
            }
          })
          .catch((res) => {
            toast.error(String(res));
          })
        : Promise.resolve(),
    ])
      .then(([prediosResult, pessoasResult]) => {
        setPredios(prediosResult.objects);
        setPessoas(pessoasResult[0]);
        setLoaded(true);
      })
      .catch((res) => {
        toast.error(String(res));
      });
  }, [id, reset]);

  const onSubmit: SubmitHandler<ReadSetorDto> = (data) => {
    setIsLoading(true);

    const dataToSave: CreateSetorDto = {
      id: id > 0 ? id : undefined,
      sigla: data.sigla || '',
      nome: data.nome || '',
      capacidade: Number(data.capacidade) || 0,
      predio: { id: Number(data.predio?.id) },
      responsavel: responsavel ? { id: responsavel.id } : null,
    } as CreateSetorDto;

    SetSetor(dataToSave)
      .then((res) => {
        toast.success(String(res));
        navigate(Links.Setor_Index);
      })
      .catch((res) => {
        toast.error(String(res));
      })
      .finally(() => setIsLoading(false));
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setModalExcluirShow(false);
    DeleteSetor(id)
      .then((res) => {
        toast.success(String(res));
        navigate(Links.Setor_Index);
      })
      .catch((res) => {
        toast.error(String(res));
      });
  };

  if (!loaded) {
    return <Loading />;
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3 className="pt-4">Setor</h3>
        </div>
      </div>
      <hr className="pb-4" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-2 mb-3">
            <label htmlFor="sigla" className="form-label">
              Sigla/Número:
            </label>
            <input
              id="sigla"
              type="text"
              title="Sigla ou número do setor/sala"
              className="form-control"
              maxLength={7}
              required
              {...register('sigla')}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="nome" className="form-label">
              Nome:
            </label>
            <input
              id="nome"
              type="text"
              title="Nome do setor"
              className="form-control"
              required
              {...register('nome')}
            />
          </div>

          <div className="col-md-2 mb-3">
            <label htmlFor="capacidade" className="form-label">
              Capacidade:
            </label>
            <input
              id="capacidade"
              type="number"
              title="Capacidade de pessoal"
              className="form-control"
              min={1}
              max={999}
              {...register('capacidade', { valueAsNumber: true })}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="predio" className="form-label">
              Unidade:
            </label>
            <select
              id="predio"
              title="Unidade"
              className="form-select"
              required
              {...register('predio.id', { valueAsNumber: true })}
            >
              <option value="">Selecione...</option>
              {predios.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="responsavel" className="form-label">
              Responsável:
            </label>
            <SelectGenerico
              id="responsavel"
              name="responsavel"
              value={responsavel as ReadPersonDto | undefined}
              onChange={(e) => setValue('responsavel', e as ReadPersonDto | undefined)}
              items={pessoas}
              getOptionLabel={(p) => p.nome!}
              getOptionValue={(p) => p.id}
              isClearable={true}
            />
          </div>

          <EditadoPorRodape
            dados={{
              editadoPor: editadoPor ? { username: editadoPor.username } : undefined,
              editadoEm: editadoEm,
            }}
          />

          <div>
            <BotoesSalvarExcluir
              isLoading={isLoading}
              texto={btnSalvar}
              textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
              id={id}
              onExcluirClick={() => setModalExcluirShow(true)}
            />
          </div>
        </div>
      </form>

      <ModalExcluir
        show={modalExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      />
      <ModalHistorico
        show={modalHistorico}
        handleHide={() => setModalHistorico(false)}
        tabela={Tabelas.setor}
        idRegistro={id}
      />
    </>
  );
}
